import { Link } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { useCart } from "@/lib/CartContext";
import { useSite } from "@/lib/SiteContext";
import { assetUrl } from "@/lib/api";
import { LogoMark } from "@/components/Logo";

export default function Cart() {
  const { items, setQty, removeItem, count } = useCart();
  const { settings } = useSite();
  const subtotal = items.reduce((sum, i) => {
    const n = parseFloat(i.price);
    return sum + (isNaN(n) ? 0 : n * i.qty);
  }, 0);

  return (
    <div className="bg-ink min-h-screen" data-testid="cart-page">
      <Seo title="Shopping Cart — Dirty Church Chronicles" description="Your Dirty Church Chronicles cart."
        path="/cart" noindex />
      <section className="pt-40 pb-24 max-w-4xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="eyebrow mb-4">Shopping Cart</p>
          <h1 className="font-display font-black uppercase text-paper text-4xl sm:text-6xl tracking-tight mb-14" data-testid="cart-title">
            Your Cart
          </h1>
        </Reveal>

        {items.length === 0 ? (
          <Reveal>
            <div className="border border-paper/10 bg-coal p-12 text-center" data-testid="cart-empty">
              <LogoMark size={48} className="mx-auto mb-6" />
              <p className="font-display text-2xl text-paper mb-3">Your cart awaits its first chronicle.</p>
              <p className="font-ui text-sm text-paper/50 mb-10 max-w-md mx-auto">
                The Kindle edition is available now on Amazon. Everything else opens when the Official Store launches.
              </p>
              <div className="flex justify-center gap-4 flex-wrap">
                <Link to="/bookstore" data-testid="cart-bookstore-link"
                  className="font-ui text-xs uppercase tracking-[0.22em] font-semibold bg-crimson text-paper px-8 py-4 rounded-full transition-colors duration-300 hover:bg-crimsonhi">
                  Official Bookstore
                </Link>
                <Link to="/merch" data-testid="cart-merch-link"
                  className="font-ui text-xs uppercase tracking-[0.22em] text-paper/80 border border-paper/25 px-8 py-4 rounded-full transition-colors duration-300 hover:border-paper/60">
                  Official Merch
                </Link>
              </div>
            </div>
          </Reveal>
        ) : (
          <Reveal>
            <div className="divide-y divide-paper/10 border-y border-paper/10" data-testid="cart-items">
              {items.map((i) => (
                <div key={i.key} className="py-6 flex items-center gap-5" data-testid={`cart-item-${i.key}`}>
                  <div className="w-16 aspect-[4/5] bg-coal border border-paper/10 shrink-0 overflow-hidden">
                    {i.image ? (
                      <img src={assetUrl(i.image)} alt={i.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><LogoMark size={22} /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-lg text-paper truncate">{i.title}</p>
                    <p className="font-ui text-xs text-paper/45 mt-1">
                      {[i.subtitle, i.variant].filter(Boolean).join(" · ")}
                      {i.price ? ` · $${i.price}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setQty(i.key, i.qty - 1)} aria-label="Decrease quantity"
                      data-testid={`cart-minus-${i.key}`}
                      className="p-2 border border-paper/20 rounded-full text-paper/60 hover:text-paper transition-colors"><Minus size={13} /></button>
                    <span className="font-ui text-sm text-paper w-6 text-center" data-testid={`cart-qty-${i.key}`}>{i.qty}</span>
                    <button onClick={() => setQty(i.key, i.qty + 1)} aria-label="Increase quantity"
                      data-testid={`cart-plus-${i.key}`}
                      className="p-2 border border-paper/20 rounded-full text-paper/60 hover:text-paper transition-colors"><Plus size={13} /></button>
                  </div>
                  <button onClick={() => removeItem(i.key)} aria-label={`Remove ${i.title}`}
                    data-testid={`cart-remove-${i.key}`}
                    className="p-2 text-paper/40 hover:text-crimsonhi transition-colors"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-8 flex-wrap gap-6">
              <p className="font-display text-xl text-paper">
                Subtotal <span className="text-gold ml-3" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
              </p>
              <div className="text-right">
                <button disabled data-testid="checkout-btn"
                  className="font-ui text-xs uppercase tracking-[0.22em] font-semibold bg-paper/15 text-paper/40 px-8 py-4 rounded-full cursor-not-allowed">
                  Checkout — Opens at Launch
                </button>
                <p className="font-ui text-xs text-paper/40 mt-3 max-w-xs">
                  Secure checkout activates when the Official Store launches. Books and merchandise ship separately;
                  taxes and shipping are calculated at checkout.
                </p>
              </div>
            </div>
          </Reveal>
        )}
      </section>
    </div>
  );
}
